import { useQuery } from "@tanstack/react-query";
import { ui } from "@patient-watch/ui";
import { Label } from "@patient-watch/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@patient-watch/ui/select";
import type { InputT } from "@patient-watch/types";
import { useMemo } from "react";
import { useLocale } from "@/features/i18n/LocaleProvider";
import { dictionaryListQueryOptions } from "../api/dictionaryQueries";
import { resolveInputOptions } from "../inputChoices";
import { useInputWithChoices } from "../useInputsWithChoices";

const NONE = "__none__";

interface Props {
    input: Pick<InputT, "input_id" | "dictionary_id" | "dictionary" | "option_rows">;
    onChange: (dictionaryId: string | null) => void;
    /** When false the panel only shows the choices. Used where the list cannot be switched. */
    canPick?: boolean;
}

/**
 * Where a choice input gets its list. A dictionary is shared and read-only here; "Custom options"
 * keeps the editors the builder always had. Picking a dictionary does not delete the custom options,
 * so switching back loses nothing.
 */
export function DictionaryChoicesPanel({ input, onChange, canPick = true }: Props) {
    const locale = useLocale();
    const list = useQuery(dictionaryListQueryOptions());
    const withRows = useInputWithChoices(input);
    const choices = useMemo(() => resolveInputOptions(withRows, locale) ?? [], [withRows, locale]);

    return (
        <div className="space-y-1.5" data-testid="dictionary-choices-panel">
            <Label className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Choices from</Label>
            {canPick && (
                <Select
                    value={input.dictionary_id ?? NONE}
                    onValueChange={(next) => onChange(next === NONE ? null : next)}
                >
                    <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Custom options" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NONE}>Custom options</SelectItem>
                        {(list.data ?? []).map((item) => (
                            <SelectItem key={item.dictionary_id} value={item.dictionary_id}>
                                {item.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
            {input.dictionary_id != null && (
                // A new tab keeps the form being built open.
                <a
                    href={`/builder/dictionaries/${input.dictionary_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                    Open dictionary
                </a>
            )}
            {input.dictionary_id != null && (
                <ul className="space-y-1 max-h-48 overflow-auto text-sm">
                    {choices.map((choice) => (
                        <li key={choice.optionId} className="flex items-center gap-2">
                            {choice.code != null && (
                                <ui.Badge variant="outline" className="text-2xs">
                                    {choice.code}
                                </ui.Badge>
                            )}
                            <span className={choice.disabled ? "text-muted-foreground" : undefined}>
                                {choice.label}
                            </span>
                        </li>
                    ))}
                    {choices.length === 0 && <li className="text-muted-foreground text-2xs">Loading choices...</li>}
                </ul>
            )}
        </div>
    );
}
